// =====================================================================
//  ADMIN CONSOLE, SHIFTS
// ---------------------------------------------------------------------
//  Working patterns, and who is on which one.
//
//  This screen decides what "late" means. Before migration 0010 that was
//  two constants in two different files that disagreed with each other, //  `LATE_CHECK_IN_HOUR = 10` in attendance.service.js and
//  `WORK_START_MINUTES = 9 * 60 + 30` in admin.service.js, so the
//  employee portal and the admin register could reach opposite verdicts
//  about the same punch. Neither could express a night shift, a
//  four-day week, or an office that opens at a different hour.
//
//  Two tabs: the patterns themselves, and the assignment history.
//  Assignments are dated rather than a single current value, because
//  payroll recalculating a past month needs the shift somebody was
//  actually on at the time, not the one they are on now.
// =====================================================================

import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Clock,
  Moon,
  Pencil,
  Plus,
  Trash2,
  UserCheck,
} from 'lucide-react';

import backendApi from '../../api/backendApi';
import DataTable from '../DataTable';
import {
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  Field,
  Grid,
  Input,
  Modal,
  Notice,
  PageHeader,
  Panel,
  PanelBody,
  PersonCell,
  Select,
  Tabs,
  Toolbar,
  ToolbarSpacer,
  toast,
} from '../ui';
import { fmtDate } from '../format';
import { cssVar } from '../theme';

// ISO day numbering, matching the `working_days` integer array on the
// server (1 = Monday ... 7 = Sunday). Deliberately not JavaScript's
// getDay(), which puts Sunday at 0, mixing the two is an easy off-by-one.
const DAYS = [
  { iso: 1, short: 'Mon' },
  { iso: 2, short: 'Tue' },
  { iso: 3, short: 'Wed' },
  { iso: 4, short: 'Thu' },
  { iso: 5, short: 'Fri' },
  { iso: 6, short: 'Sat' },
  { iso: 7, short: 'Sun' },
];

const EMPTY_SHIFT = {
  name: '',
  code: '',
  start_time: '09:30',
  end_time: '18:30',
  break_minutes: '60',
  grace_minutes: '15',
  is_night_shift: false,
  working_days: [1, 2, 3, 4, 5],
};

const EMPTY_ASSIGNMENT = {
  user_id: '',
  shift_id: '',
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '',
};

const hhmm = (value) => (value ? String(value).slice(0, 5) : ',');

/** Scheduled hours, less the unpaid break. Handles a shift past midnight. */
const shiftLength = (shift) => {
  if (!shift?.start_time || !shift?.end_time) return null;
  const [sh, sm] = hhmm(shift.start_time).split(':').map(Number);
  const [eh, em] = hhmm(shift.end_time).split(':').map(Number);

  let minutes = eh * 60 + em - (sh * 60 + sm);
  // A negative span means the shift crosses midnight.
  if (minutes <= 0) minutes += 24 * 60;
  minutes -= Number(shift.break_minutes) || 0;

  return Math.max(0, minutes) / 60;
};

/** The working-day picker, also used read-only in the table. */
function DayPicker({ value = [], onChange, readOnly = false }) {
  const selected = new Set((value || []).map(Number));

  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {DAYS.map((day) => {
        const on = selected.has(day.iso);
        const style = {
          minWidth: 38,
          padding: '3px 6px',
          borderRadius: 'var(--ac-r-sm)',
          border: `1px solid ${on ? 'var(--ac-accent-bd)' : cssVar.line}`,
          background: on ? 'var(--ac-accent-bg)' : cssVar.panel,
          color: on ? 'var(--ac-accent-fg)' : cssVar.inkFaint,
          fontSize: 11.5,
          textAlign: 'center',
          font: 'inherit',
          fontWeight: on ? 600 : 400,
          cursor: readOnly ? 'default' : 'pointer',
        };

        if (readOnly) {
          return (
            <span key={day.iso} style={style}>
              {day.short}
            </span>
          );
        }

        return (
          <button
            key={day.iso}
            type="button"
            aria-pressed={on}
            style={style}
            onClick={() => {
              const next = new Set(selected);
              if (next.has(day.iso)) next.delete(day.iso);
              else next.add(day.iso);
              onChange([...next].sort((a, b) => a - b));
            }}
          >
            {day.short}
          </button>
        );
      })}
    </div>
  );
}

export default function ShiftsPage({ can }) {
  const queryClient = useQueryClient();
  const canManage = can('shift.manage');

  const [tab, setTab] = useState('patterns');
  const [shiftForm, setShiftForm] = useState(null);
  const [assignForm, setAssignForm] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [formError, setFormError] = useState('');

  /* ------------------------------ data ------------------------------- */

  const { data: shiftData, isLoading: shiftsLoading } = useQuery({
    queryKey: ['admin', 'shifts'],
    queryFn: () => backendApi.entities.Shift.page({ limit: 200 }),
  });
  const shifts = useMemo(() => shiftData?.rows || [], [shiftData]);

  const { data: assignData, isLoading: assignLoading } = useQuery({
    queryKey: ['admin', 'shift-assignments'],
    queryFn: () => backendApi.entities.ShiftAssignment.page({ limit: 500 }),
  });
  const assignments = useMemo(() => assignData?.rows || [], [assignData]);

  const { data: peopleData } = useQuery({
    queryKey: ['admin', 'people-lite'],
    queryFn: () => backendApi.entities.Employee.page({ limit: 1000, sort: 'full_name', order: 'asc' }),
  });
  const people = useMemo(() => peopleData?.rows || [], [peopleData]);

  const peopleById = useMemo(() => new Map(people.map((p) => [String(p.id), p])), [people]);
  const shiftById = useMemo(() => new Map(shifts.map((s) => [String(s.id), s])), [shifts]);

  const peopleOptions = people.map((p) => ({
    value: String(p.id),
    label: `${p.full_name}${p.department ? ` · ${p.department}` : ''}`,
  }));
  const shiftOptions = shifts.map((s) => ({
    value: String(s.id),
    label: `${s.name} (${hhmm(s.start_time)},${hhmm(s.end_time)})`,
  }));

  // Anyone with no dated assignment falls back to the tenant's default
  // shift on the register. Worth stating, because otherwise "why is this
  // person marked late at 09:45" has no visible answer.
  const unassignedCount = useMemo(() => {
    const assigned = new Set(assignments.map((a) => String(a.user_id)));
    return people.filter((p) => !assigned.has(String(p.id))).length;
  }, [people, assignments]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin'] });

  /* ---------------------------- mutations ---------------------------- */

  const saveShift = useMutation({
    mutationFn: (form) => {
      const body = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        start_time: form.start_time,
        end_time: form.end_time,
        break_minutes: Number(form.break_minutes) || 0,
        grace_minutes: Number(form.grace_minutes) || 0,
        is_night_shift: form.is_night_shift,
        working_days: form.working_days,
      };
      return shiftForm?.id
        ? backendApi.entities.Shift.update(shiftForm.id, body)
        : backendApi.entities.Shift.create(body);
    },
    onSuccess: () => {
      toast.success(shiftForm?.id ? 'Shift updated' : 'Shift created');
      setShiftForm(null);
      refresh();
    },
    onError: (err) => setFormError(err.message || 'Could not save this shift'),
  });

  const saveAssignment = useMutation({
    mutationFn: (form) => {
      const body = {
        user_id: Number(form.user_id),
        shift_id: Number(form.shift_id),
        effective_from: form.effective_from,
        // Blank means "still current", stored as NULL.
        effective_to: form.effective_to || null,
      };
      return assignForm?.id
        ? backendApi.entities.ShiftAssignment.update(assignForm.id, body)
        : backendApi.entities.ShiftAssignment.create(body);
    },
    onSuccess: () => {
      toast.success('Assignment saved');
      setAssignForm(null);
      refresh();
    },
    onError: (err) => setFormError(err.message || 'Could not save this assignment'),
  });

  const remove = useMutation({
    mutationFn: ({ kind, row }) =>
      kind === 'shift'
        ? backendApi.entities.Shift.delete(row.id)
        : backendApi.entities.ShiftAssignment.delete(row.id),
    onSuccess: () => {
      toast.success('Removed');
      setPendingDelete(null);
      refresh();
    },
    onError: (err) => toast.error(err.message || 'Could not remove this'),
  });

  /* ----------------------------- columns ----------------------------- */

  const shiftColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Shift',
        width: 190,
        render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <span style={{ fontWeight: 600, color: cssVar.inkStrong }}>{row.name}</span>
            {row.is_night_shift ? (
              <Chip tone="violet" title="Crosses midnight">
                <Moon size={10} style={{ marginRight: 3 }} />
                Night
              </Chip>
            ) : null}
            {row.is_active === false ? <Chip tone="negative">Inactive</Chip> : null}
          </span>
        ),
        csv: (row) => row.name,
      },
      {
        key: 'code',
        label: 'Code',
        width: 80,
        render: (row) => row.code || <span style={{ color: cssVar.inkFaint }}>,</span>,
      },
      {
        key: 'start_time',
        label: 'Hours',
        width: 130,
        render: (row) => (
          <span className="ac-mono">
            {hhmm(row.start_time)},{hhmm(row.end_time)}
          </span>
        ),
        csv: (row) => `${hhmm(row.start_time)}-${hhmm(row.end_time)}`,
      },
      {
        key: 'length',
        label: 'Paid',
        width: 85,
        align: 'right',
        sortable: false,
        render: (row) => {
          const hours = shiftLength(row);
          return hours == null ? ',' : <span className="ac-mono">{hours.toFixed(1)}h</span>;
        },
        csv: (row) => shiftLength(row)?.toFixed(1) ?? '',
      },
      {
        key: 'grace_minutes',
        label: 'Grace',
        width: 80,
        align: 'right',
        // The number that actually decides lateness, so it gets a column of
        // its own rather than hiding in an edit form.
        render: (row) => <span className="ac-mono">{row.grace_minutes}m</span>,
      },
      {
        key: 'late_after',
        label: 'Late after',
        width: 100,
        align: 'right',
        sortable: false,
        render: (row) => {
          const [h, m] = hhmm(row.start_time).split(':').map(Number);
          const total = h * 60 + m + (Number(row.grace_minutes) || 0);
          const label = `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(
            total % 60
          ).padStart(2, '0')}`;
          return <span className="ac-mono">{label}</span>;
        },
        csv: () => '',
      },
      {
        key: 'working_days',
        label: 'Days',
        sortable: false,
        render: (row) => <DayPicker value={row.working_days} readOnly />,
        csv: (row) => (row.working_days || []).join('/'),
      },
      {
        key: 'assigned',
        label: 'People',
        width: 80,
        align: 'right',
        sortable: false,
        render: (row) => {
          const n = assignments.filter((a) => String(a.shift_id) === String(row.id)).length;
          return n ? n : <span style={{ color: cssVar.inkFaint }}>,</span>;
        },
        csv: (row) => assignments.filter((a) => String(a.shift_id) === String(row.id)).length,
      },
    ],
    [assignments]
  );

  const assignmentColumns = useMemo(
    () => [
      {
        key: 'user_id',
        label: 'Employee',
        render: (row) => {
          const person = peopleById.get(String(row.user_id));
          return person ? (
            <PersonCell name={person.full_name} sub={person.department} email={person.email} />
          ) : (
            <Chip tone="negative">Employee removed</Chip>
          );
        },
        csv: (row) => peopleById.get(String(row.user_id))?.full_name || '',
      },
      {
        key: 'shift_id',
        label: 'Shift',
        width: 190,
        render: (row) => {
          const shift = shiftById.get(String(row.shift_id));
          return shift ? (
            <span>
              {shift.name}{' '}
              <span className="ac-mono" style={{ color: cssVar.inkMuted }}>
                {hhmm(shift.start_time)},{hhmm(shift.end_time)}
              </span>
            </span>
          ) : (
            <Chip tone="negative">Shift removed</Chip>
          );
        },
        csv: (row) => shiftById.get(String(row.shift_id))?.name || '',
      },
      {
        key: 'effective_from',
        label: 'From',
        width: 130,
        render: (row) => fmtDate(row.effective_from),
      },
      {
        key: 'effective_to',
        label: 'Until',
        width: 140,
        render: (row) =>
          row.effective_to ? (
            fmtDate(row.effective_to)
          ) : (
            <Chip tone="positive">Current</Chip>
          ),
        csv: (row) => row.effective_to || 'current',
      },
    ],
    [peopleById, shiftById]
  );

  const rowActions = (kind) => (row) =>
    canManage ? (
      <div style={{ display: 'flex', gap: 4 }}>
        <Button
          size="sm"
          variant="ghost"
          icon={Pencil}
          aria-label="Edit"
          onClick={() => {
            setFormError('');
            if (kind === 'shift') {
              setShiftForm({
                id: row.id,
                name: row.name || '',
                code: row.code || '',
                start_time: hhmm(row.start_time),
                end_time: hhmm(row.end_time),
                break_minutes: String(row.break_minutes ?? 0),
                grace_minutes: String(row.grace_minutes ?? 0),
                is_night_shift: row.is_night_shift === true,
                working_days: row.working_days || [1, 2, 3, 4, 5],
              });
            } else {
              setAssignForm({
                id: row.id,
                user_id: String(row.user_id || ''),
                shift_id: String(row.shift_id || ''),
                effective_from: String(row.effective_from || '').slice(0, 10),
                effective_to: row.effective_to ? String(row.effective_to).slice(0, 10) : '',
              });
            }
          }}
        />
        <Button
          size="sm"
          variant="ghost"
          icon={Trash2}
          aria-label="Remove"
          onClick={() => setPendingDelete({ kind, row })}
        />
      </div>
    ) : undefined;

  const shiftFormLength = shiftForm ? shiftLength(shiftForm) : null;
  // end <= start means the span wraps past midnight, which the server
  // tracks with an explicit flag rather than inferring.
  const looksLikeNightShift =
    shiftForm &&
    shiftForm.end_time &&
    shiftForm.start_time &&
    shiftForm.end_time <= shiftForm.start_time;

  return (
    <div className="ac-page">
      <PageHeader
        title="Shifts"
        sub="Working patterns, and the grace period that decides who counts as late."
      />

      {!canManage ? (
        <div style={{ marginBottom: 14 }}>
          <Notice tone="info" title="You have read-only access here">
            Defining shifts and assigning people to them requires the shift
            management permission, which sits with administrators and HR.
          </Notice>
        </div>
      ) : null}

      <Tabs
        items={[
          { key: 'patterns', label: 'Patterns', count: shifts.length, icon: Clock },
          { key: 'assignments', label: 'Assignments', count: assignments.length, icon: UserCheck },
        ]}
        value={tab}
        onChange={setTab}
      />

      {tab === 'patterns' ? (
        <Panel>
          <PanelBody flush>
            <DataTable
              columns={shiftColumns}
              rows={shifts}
              loading={shiftsLoading}
              getRowId={(row) => row.id}
              paginate={false}
              emptyIcon={Clock}
              emptyTitle="No shifts yet"
              emptyText="A shift defines working hours and the grace period before someone is marked late."
              rowActions={rowActions('shift')}
              toolbar={
                <Toolbar>
                  <span style={{ fontSize: 12, color: cssVar.inkMuted }}>
                    “Late after” is the shift start plus its grace period.
                  </span>
                  <ToolbarSpacer />
                  {canManage ? (
                    <Button
                      variant="primary"
                      icon={Plus}
                      onClick={() => {
                        setFormError('');
                        setShiftForm({ ...EMPTY_SHIFT });
                      }}
                    >
                      Add shift
                    </Button>
                  ) : null}
                </Toolbar>
              }
            />
          </PanelBody>
        </Panel>
      ) : null}

      {tab === 'assignments' ? (
        <>
          {unassignedCount > 0 ? (
            <div style={{ marginBottom: 14 }}>
              <Notice tone="default" title={`${unassignedCount} people have no explicit shift`}>
                They fall back to the organization's default shift on the attendance register.
                Assign one here only if their hours differ.
              </Notice>
            </div>
          ) : null}

          <Panel>
            <PanelBody flush>
              <DataTable
                columns={assignmentColumns}
                rows={assignments}
                loading={assignLoading}
                getRowId={(row) => row.id}
                defaultSort={{ key: 'effective_from', dir: 'desc' }}
                emptyIcon={UserCheck}
                emptyTitle="Nobody is explicitly assigned"
                emptyText="Everyone is on the default shift. Add an assignment for anyone whose hours differ."
                rowActions={rowActions('assignment')}
                toolbar={
                  <Toolbar>
                    <span style={{ fontSize: 12, color: cssVar.inkMuted }}>
                      Assignments are dated, so a past month is always read against the shift that
                      applied at the time.
                    </span>
                    <ToolbarSpacer />
                    {canManage ? (
                      <Button
                        variant="primary"
                        icon={Plus}
                        disabled={shifts.length === 0}
                        onClick={() => {
                          setFormError('');
                          setAssignForm({ ...EMPTY_ASSIGNMENT });
                        }}
                      >
                        Assign a shift
                      </Button>
                    ) : null}
                  </Toolbar>
                }
              />
            </PanelBody>
          </Panel>
        </>
      ) : null}

      {/* ------------------------------ FORMS ------------------------------ */}

      <Modal
        open={Boolean(shiftForm)}
        onClose={() => setShiftForm(null)}
        title={shiftForm?.id ? `Edit “${shiftForm.name}”` : 'Add a shift'}
        size="sm"
        footer={
          <>
            <Button onClick={() => setShiftForm(null)} disabled={saveShift.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={Check}
              loading={saveShift.isPending}
              disabled={!shiftForm?.name?.trim() || !shiftForm?.working_days?.length}
              onClick={() => saveShift.mutate(shiftForm)}
            >
              Save
            </Button>
          </>
        }
      >
        {formError ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="negative" title="Could not save">
              {formError}
            </Notice>
          </div>
        ) : null}

        {shiftForm ? (
          <>
            <Grid cols={2}>
              <Field label="Name" required>
                <Input
                  value={shiftForm.name}
                  onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                  autoFocus
                />
              </Field>
              <Field label="Code" hint="Short identifier, e.g. GEN">
                <Input
                  value={shiftForm.code}
                  onChange={(e) => setShiftForm({ ...shiftForm, code: e.target.value })}
                />
              </Field>
            </Grid>

            <Grid cols={2}>
              <Field label="Starts" required>
                <Input
                  type="time"
                  value={shiftForm.start_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, start_time: e.target.value })}
                />
              </Field>
              <Field label="Ends" required>
                <Input
                  type="time"
                  value={shiftForm.end_time}
                  onChange={(e) => setShiftForm({ ...shiftForm, end_time: e.target.value })}
                />
              </Field>
            </Grid>

            <Grid cols={2}>
              <Field label="Unpaid break" hint="Minutes, deducted from hours worked">
                <Input
                  type="number"
                  min="0"
                  value={shiftForm.break_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, break_minutes: e.target.value })}
                />
              </Field>
              <Field
                label="Grace period"
                hint={`Minutes. Late after ${(() => {
                  const [h, m] = (shiftForm.start_time || '00:00').split(':').map(Number);
                  const total = h * 60 + m + (Number(shiftForm.grace_minutes) || 0);
                  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(
                    total % 60
                  ).padStart(2, '0')}`;
                })()}`}
              >
                <Input
                  type="number"
                  min="0"
                  value={shiftForm.grace_minutes}
                  onChange={(e) => setShiftForm({ ...shiftForm, grace_minutes: e.target.value })}
                />
              </Field>
            </Grid>

            <Field label="Working days" required hint="At least one">
              <DayPicker
                value={shiftForm.working_days}
                onChange={(working_days) => setShiftForm({ ...shiftForm, working_days })}
              />
            </Field>

            <div style={{ fontSize: 12, color: cssVar.inkMuted, marginTop: 2 }}>
              {shiftFormLength != null
                ? `${shiftFormLength.toFixed(1)} paid hours per day, ${
                    shiftForm.working_days.length
                  } day${shiftForm.working_days.length === 1 ? '' : 's'} a week.`
                : null}
            </div>

            <div style={{ marginTop: 10 }}>
              <Checkbox
                checked={shiftForm.is_night_shift}
                onChange={(e) =>
                  setShiftForm({ ...shiftForm, is_night_shift: e.target.checked })
                }
                label="Crosses midnight (night shift)"
              />
            </div>

            {looksLikeNightShift && !shiftForm.is_night_shift ? (
              <div style={{ marginTop: 10 }}>
                <Notice tone="warning" title="These hours end before they start">
                  That usually means the shift runs past midnight. Tick the night shift box so a
                  punch after 00:00 is counted against the previous day.
                </Notice>
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(assignForm)}
        onClose={() => setAssignForm(null)}
        title={assignForm?.id ? 'Edit assignment' : 'Assign a shift'}
        size="sm"
        footer={
          <>
            <Button onClick={() => setAssignForm(null)} disabled={saveAssignment.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              icon={Check}
              loading={saveAssignment.isPending}
              disabled={
                !assignForm?.user_id || !assignForm?.shift_id || !assignForm?.effective_from
              }
              onClick={() => saveAssignment.mutate(assignForm)}
            >
              Save
            </Button>
          </>
        }
      >
        {formError ? (
          <div style={{ marginBottom: 12 }}>
            <Notice tone="negative" title="Could not save">
              {formError}
            </Notice>
          </div>
        ) : null}

        {assignForm ? (
          <>
            <Field label="Employee" required>
              <Select
                options={peopleOptions}
                placeholder="Choose someone"
                value={assignForm.user_id}
                onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })}
              />
            </Field>

            <Field label="Shift" required>
              <Select
                options={shiftOptions}
                placeholder="Choose a shift"
                value={assignForm.shift_id}
                onChange={(e) => setAssignForm({ ...assignForm, shift_id: e.target.value })}
              />
            </Field>

            <Grid cols={2}>
              <Field label="Effective from" required>
                <Input
                  type="date"
                  value={assignForm.effective_from}
                  onChange={(e) =>
                    setAssignForm({ ...assignForm, effective_from: e.target.value })
                  }
                />
              </Field>
              <Field label="Until" hint="Leave empty while current">
                <Input
                  type="date"
                  value={assignForm.effective_to}
                  onChange={(e) => setAssignForm({ ...assignForm, effective_to: e.target.value })}
                />
              </Field>
            </Grid>

            {assignForm.effective_to &&
            assignForm.effective_to < assignForm.effective_from ? (
              <Notice tone="negative">The end date is before the start date.</Notice>
            ) : null}
          </>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => remove.mutate(pendingDelete)}
        loading={remove.isPending}
        title={
          pendingDelete?.kind === 'shift'
            ? `Remove “${pendingDelete?.row?.name}”?`
            : 'Remove this assignment?'
        }
        confirmLabel="Remove"
        message={
          pendingDelete?.kind === 'shift'
            ? 'Anyone assigned to this shift falls back to the organization’s default shift, which changes how their punctuality is judged. Past attendance is not recalculated.'
            : 'This person falls back to the default shift from now on. Their past attendance is unaffected.'
        }
      />
    </div>
  );
}
